import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import "./PopoverManager.scss";

import AppT9N from "../../../t9n/App/App.json";
import PopoverManagerViewModel from "./PopoverViewModel";
import { watch, whenOnce } from "@arcgis/core/core/reactiveUtils";
import NavBarViewModel from "src/app/ui/NavBar/NavBarViewModel";
import { isEnterprise } from "../../../app/utils/appUtils";
import { substitute } from "@arcgis/core/intl";

export enum PopoverIds {
  popover1 = "popover1",
  popover2 = "popover2",
  popover3 = "popover3",
  popover4 = "popover4",
  popover5 = "popover5",
  popover6 = "popover6",
}

export const ReleaseTag = "R1.2025_Opened";

@subclass("Landing.widgets.PopoverOnboarding.Popover")
export default class PopoverManager extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  viewModel: PopoverManagerViewModel = new PopoverManagerViewModel({});

  @property()
  currentPopoverIndex: number = 0;

  @property()
  isClosed: boolean = localStorage.getItem(ReleaseTag) === "true";

  @property()
  learnMoreSignalFromManager: boolean;

  @property()
  navbarVM: NavBarViewModel;
  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------
  constructor(props?: any) {
    super(props);
  }

  postInitialize() {
    localStorage.setItem(ReleaseTag, "true");
    whenOnce(() => this.appMessages).then(() => {
      this.viewModel.appMessages = this.appMessages;
      this.viewModel.createPopovers();
    });

    watch(
      () => this.currentPopoverIndex,
      async () => {
        let timeoutAmt;
        switch (this.currentPopoverIndex) {
          case 0:
            this.navbarVM.setNavbarRoute("browseAll");
            break;
          case 1:
            this.navbarVM.setNavbarRoute("browseAll");
            break;
          case 2:
            this.navbarVM.setNavbarRoute("browseAll");
            break;
          case 3:
            this.navbarVM.setNavbarRoute("browseAll");
            setTimeout(() => {
              const firstCardDD = document.getElementsByClassName(
                "app-type-tile__dropdown-options"
              )[0] as HTMLCalciteDropdownElement;
              firstCardDD?.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              const firstCardDDOption = firstCardDD
                .children[0] as HTMLCalciteButtonElement;
              setTimeout(() => {
                firstCardDDOption?.setFocus();
                firstCardDDOption?.click();
              });
            }, 500);
            break;
          case 4:
            this.navbarVM.setNavbarRoute("myApps");
            timeoutAmt = 800;
            if(this.navbarVM.myAppsDataVM?.contentLevel !== "myApps") {
              this.navbarVM.myAppsDataVM.setContentLevelAndRequery("myApps");
              await new Promise((resolve) => {
                const interval = setInterval(() => {
                  if (!this.navbarVM.myAppsDataVM.isMyAppsLoading) {
                    clearInterval(interval);
                    resolve(true);
                  }
                }, 100);
              });
              timeoutAmt = 1000;
            }
            setTimeout(() => {
              const firstCardSplitBtn = document.getElementsByClassName(
                "app-tile__dropdown-options"
              )[0] as HTMLCalciteSplitButtonElement;
              const dropdown =
                firstCardSplitBtn?.shadowRoot?.querySelector(
                  "calcite-dropdown"
                );
              const dropdownButton = dropdown
                .children[0] as HTMLCalciteButtonElement;
              setTimeout(() => {
                dropdownButton?.setFocus();
                dropdownButton?.click();
                this.isClosed = true;
                setTimeout(() => {
                this.isClosed = false; 
                });
              });
            }, timeoutAmt);
            break;
          case 5:
            this.navbarVM.setNavbarRoute("myApps");
            timeoutAmt = 800;
            if(this.navbarVM.myAppsDataVM?.contentLevel !== "myApps") {
              this.navbarVM.myAppsDataVM.setContentLevelAndRequery("myApps");
              await new Promise((resolve) => {
                const interval = setInterval(() => {
                  if (!this.navbarVM.myAppsDataVM.isMyAppsLoading) {
                    clearInterval(interval);
                    resolve(true);
                  }
                }, 100);
              });
              timeoutAmt = 1000;
            }
            setTimeout(() => {
              const firstCardSplitBtn = document.getElementsByClassName(
                "app-tile__dropdown-options"
              )[0] as HTMLCalciteSplitButtonElement;
              const dropdown =
                firstCardSplitBtn?.shadowRoot?.querySelector(
                  "calcite-dropdown"
                );
              const dropdownButton = dropdown
                .children[0] as HTMLCalciteButtonElement;
              setTimeout(() => {
                dropdownButton?.setFocus();
                dropdownButton?.click();
                this.isClosed = true;
                setTimeout(() => {
                  this.isClosed = false; 
                });

                setTimeout(() => {
                  const saveCopyOption: HTMLCalciteDropdownItemElement =
                    document.querySelector("#popover6");
                  saveCopyOption.setFocus();
                  saveCopyOption.focus();
                }, 200);
              });
            }, timeoutAmt);
            break;
          default:
            break;
        }
      }
    );
  }

  render() {
    const { refElementId, title, content, icon } =
      this.viewModel.popovers[this.currentPopoverIndex] || {};
    const { next, of } = this.appMessages.popoverOnboardingExtra;
    return !isEnterprise(this.navbarVM.portal) && !this.isClosed ? (
      <calcite-popover
        key={"popover-manager" + this.currentPopoverIndex}
        class="onboarding-popover"
        label={title}
        reference-element={refElementId}
        closable
        open
        afterCreate={(el) => {
          this.listenForClose(el);
        }}
      >
        <div class="onboarding-popover-root">
          <div class="onboarding-header">
            <calcite-icon icon={icon}></calcite-icon> <h3>{title}</h3>
          </div>
          <p>{content}</p>
          <div class="footer">
            <span>
              {substitute(of, {
                current: this.currentPopoverIndex + 1,
                total: this.viewModel.popovers.length,
              })}
            </span>
            <div>
              {this.currentPopoverIndex ===
              this.viewModel.popovers.length - 1 ? null : (
                <calcite-button
                  key="nextPopover"
                  id="nextPopoverBtn"
                  appearance="outline"
                  onclick={this.nextPopover.bind(this)}
                >
                  {next}
                </calcite-button>
              )}
            </div>
          </div>
        </div>
      </calcite-popover>
    ) : (
      <div key="empty-node"></div>
    );
  }

  private listenForClose(el: HTMLCalcitePopoverElement) {
    el.addEventListener("calcitePopoverClose", () => {
      this.isClosed = true;
    });
  }

  private openLearnMore() {
    this.learnMoreSignalFromManager = true;
  }

  private nextPopover() {
    if (this.currentPopoverIndex < this.viewModel.popovers.length - 1) {
      this.currentPopoverIndex++;
    }

    setTimeout(() => {
      const nextPopoverBtn: HTMLCalciteButtonElement = document.getElementById(
        "nextPopoverBtn"
      ) as any;
      nextPopoverBtn?.focus();
      nextPopoverBtn?.setFocus();
    }, 100);
  }
}
